export interface MatrixSelectionPair {
	pupilId: string;
	goalId: string;
}

/**
 * Selection state for the goals-by-pupils matrix: a set of pupil/goal pairs.
 * Pure data, no Vue. The matrix passes in the currently visible pupil and goal ids
 * for row toggles and pruning so this class never needs to know about goal structures.
 */
export class MatrixSelection {

	private keys: Set<string> = new Set();

	private static key(pupilId: string, goalId: string): string {
		return `${pupilId}:${goalId}`;
	}

	private static parse(key: string): MatrixSelectionPair {
		const separator = key.indexOf(':');
		return {
			pupilId: key.substring(0, separator),
			goalId: key.substring(separator + 1),
		};
	}

	has(pupilId: string, goalId: string): boolean {
		return this.keys.has(MatrixSelection.key(pupilId, goalId));
	}

	toggleCell(pupilId: string, goalId: string): void {
		const key = MatrixSelection.key(pupilId, goalId);
		if (this.keys.has(key)) {
			this.keys.delete(key);
		} else {
			this.keys.add(key);
		}
	}

	isGoalRowSelected(goalId: string, pupilIds: string[]): boolean {
		return pupilIds.length > 0 && pupilIds.every((pupilId) => this.has(pupilId, goalId));
	}

	/** Select every cell in the row, unless all are already selected: then deselect them all. */
	toggleGoalRow(goalId: string, pupilIds: string[]): void {
		const deselect = this.isGoalRowSelected(goalId, pupilIds);
		pupilIds.forEach((pupilId) => {
			const key = MatrixSelection.key(pupilId, goalId);
			if (deselect) {
				this.keys.delete(key);
			} else {
				this.keys.add(key);
			}
		});
	}

	/** Drop pairs whose goal is not in the given list (goal left the view after a filter change). */
	pruneToGoals(goalIds: string[]): void {
		const visible = new Set(goalIds);
		this.pairs()
			.filter((pair) => !visible.has(pair.goalId))
			.forEach((pair) => this.keys.delete(MatrixSelection.key(pair.pupilId, pair.goalId)));
	}

	clear(): void {
		this.keys.clear();
	}

	get count(): number {
		return this.keys.size;
	}

	pairs(): MatrixSelectionPair[] {
		return Array.from(this.keys).map(MatrixSelection.parse);
	}

	pupilIds(): string[] {
		return Array.from(new Set(this.pairs().map((pair) => pair.pupilId)));
	}

	goalIds(): string[] {
		return Array.from(new Set(this.pairs().map((pair) => pair.goalId)));
	}
}
